import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";
import { Toaster } from "sonner";

import Login from "./pages/Login";
import AdminLogin from "./pages/AdminLogin";
import AdminResetPassword from "./pages/AdminResetPassword";
import ResetPassword from "./pages/ResetPassword";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import About from "./pages/About";
import Terms from "./pages/Terms";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CancellationPolicy from "./pages/CancellationPolicy";
import ContactUs from "./pages/ContactUs";
import Home from "./pages/Home";
import CentreDetail from "./pages/CentreDetail";
import Book from "./pages/Book";
import MyBookings from "./pages/MyBookings";
import Profile from "./pages/Profile";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import Community from "./pages/Community";
import CommunityGroup from "./pages/CommunityGroup";
import CommunityGame from "./pages/CommunityGame";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminCentres from "./pages/admin/AdminCentres";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminEventDetail from "./pages/admin/AdminEventDetail";
import AdminManagers from "./pages/admin/AdminManagers";
import AdminSlots from "./pages/admin/AdminSlots";
import AdminBroadcasts from "./pages/admin/AdminBroadcasts";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminReports from "./pages/admin/AdminReports";
import AdminRefunds from "./pages/admin/AdminRefunds";
import AdminLoyalty from "./pages/admin/AdminLoyalty";
import AdminAcademy from "./pages/admin/AdminAcademy";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminAdmins from "./pages/admin/AdminAdmins";
import AdminApiKeys from "./pages/admin/AdminApiKeys";
import AdminContactMessages from "./pages/admin/AdminContactMessages";

import ManagerDashboard from "./pages/manager/ManagerDashboard";
import ManagerBookings from "./pages/manager/ManagerBookings";
import ManagerSlots from "./pages/manager/ManagerSlots";

import PreviewModeBanner from "./components/PreviewModeBanner";

function withLayout(node) {
    return <AppLayout>{node}</AppLayout>;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
    const location = useLocation();
    // Synchronously detect Google auth callback hash to avoid race with AuthProvider
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin-reset-password" element={<AdminResetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/about" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            <Route path="/" element={
                <ProtectedRoute>{withLayout(<Home />)}</ProtectedRoute>
            } />
            <Route path="/centres/:id" element={
                <ProtectedRoute>{withLayout(<CentreDetail />)}</ProtectedRoute>
            } />
            <Route path="/book/:turfId" element={
                <ProtectedRoute>{withLayout(<Book />)}</ProtectedRoute>
            } />
            <Route path="/book/centre/:centreId/facility/:facilityId" element={
                <ProtectedRoute>{withLayout(<Book />)}</ProtectedRoute>
            } />
            <Route path="/bookings" element={
                <ProtectedRoute>{withLayout(<MyBookings />)}</ProtectedRoute>
            } />
            <Route path="/profile" element={
                <ProtectedRoute>{withLayout(<Profile />)}</ProtectedRoute>
            } />
            <Route path="/events" element={
                <ProtectedRoute>{withLayout(<Events />)}</ProtectedRoute>
            } />
            <Route path="/events/:id" element={
                <ProtectedRoute>{withLayout(<EventDetail />)}</ProtectedRoute>
            } />
            <Route path="/community" element={
                <ProtectedRoute>{withLayout(<Community />)}</ProtectedRoute>
            } />
            <Route path="/community/groups/:id" element={
                <ProtectedRoute>{withLayout(<CommunityGroup />)}</ProtectedRoute>
            } />
            <Route path="/community/games/:id" element={
                <ProtectedRoute>{withLayout(<CommunityGame />)}</ProtectedRoute>
            } />

            <Route path="/admin" element={
                <ProtectedRoute adminOnly>{<AdminDashboard />}</ProtectedRoute>
            } />
            <Route path="/admin/bookings" element={
                <ProtectedRoute adminOnly>{<AdminBookings />}</ProtectedRoute>
            } />
            <Route path="/admin/centres" element={
                <ProtectedRoute adminOnly>{<AdminCentres />}</ProtectedRoute>
            } />
            <Route path="/admin/coupons" element={
                <ProtectedRoute adminOnly>{<AdminCoupons />}</ProtectedRoute>
            } />
            <Route path="/admin/customers" element={
                <ProtectedRoute adminOnly>{<AdminCustomers />}</ProtectedRoute>
            } />
            <Route path="/admin/events" element={
                <ProtectedRoute adminOnly>{<AdminEvents />}</ProtectedRoute>
            } />
            <Route path="/admin/events/:id" element={
                <ProtectedRoute adminOnly>{<AdminEventDetail />}</ProtectedRoute>
            } />
            <Route path="/admin/managers" element={
                <ProtectedRoute adminOnly>{<AdminManagers />}</ProtectedRoute>
            } />
            <Route path="/admin/slots" element={
                <ProtectedRoute adminOnly>{<AdminSlots />}</ProtectedRoute>
            } />
            <Route path="/admin/broadcasts" element={
                <ProtectedRoute adminOnly>{<AdminBroadcasts />}</ProtectedRoute>
            } />
            <Route path="/admin/reports" element={
                <ProtectedRoute adminOnly>{<AdminReports />}</ProtectedRoute>
            } />
            <Route path="/admin/refunds" element={
                <ProtectedRoute adminOnly>{<AdminRefunds />}</ProtectedRoute>
            } />
            <Route path="/admin/loyalty" element={
                <ProtectedRoute adminOnly>{<AdminLoyalty />}</ProtectedRoute>
            } />
            <Route path="/admin/academy" element={
                <ProtectedRoute adminOnly>{<AdminAcademy />}</ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
                <ProtectedRoute adminOnly>{<AdminSettings />}</ProtectedRoute>
            } />
            <Route path="/admin/contact-messages" element={
                <ProtectedRoute adminOnly>{<AdminContactMessages />}</ProtectedRoute>
            } />
            <Route path="/admin/banners" element={
                <ProtectedRoute adminOnly>{<AdminBanners />}</ProtectedRoute>
            } />
            <Route path="/admin/admins" element={
                <ProtectedRoute adminOnly>{<AdminAdmins />}</ProtectedRoute>
            } />
            <Route path="/admin/api-keys" element={
                <ProtectedRoute adminOnly>{<AdminApiKeys />}</ProtectedRoute>
            } />

            <Route path="/manager" element={
                <ProtectedRoute staffOnly>{<ManagerDashboard />}</ProtectedRoute>
            } />
            <Route path="/manager/bookings" element={
                <ProtectedRoute staffOnly>{<ManagerBookings />}</ProtectedRoute>
            } />
            <Route path="/manager/slots" element={
                <ProtectedRoute staffOnly>{<ManagerSlots />}</ProtectedRoute>
            } />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <Toaster theme="dark" richColors position="top-center" />
                <BrowserRouter>
                    <PreviewModeBanner />
                    <AppRouter />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
